import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "../../contexts/AppContext";
import { useScenarios } from "../../hooks/useScenarios";
import { useTerminology } from "../../hooks/useTerminology";
import { useVisibleScenarios } from "../../hooks/useVisibleScenarios";

import { DependencyType } from "../../types/scenarios";
import { ScenarioDependencyPicker } from "../scenarios/ScenarioDependencyPicker";

interface CreateScenarioModalProps {
  open: boolean;
  onClose: () => void;
  onScenarioCreated?: (scenarioId: string) => void;
  initialTemplate?: "growth" | "cost-cutting" | "crisis";
}

const TEMPLATE_DEFAULTS: Record<
  "growth" | "cost-cutting" | "crisis",
  {
    name: string;
    description: string;
    category: "growth" | "cost-cutting" | "crisis";
  }
> = {
  growth: {
    name: "Growth Scenario",
    description: "Model new customer wins and revenue growth",
    category: "growth",
  },
  "cost-cutting": {
    name: "Cost Reduction Plan",
    description: "Explore cost-cutting opportunities",
    category: "cost-cutting",
  },
  crisis: {
    name: "Crisis Plan",
    description: "Prepare for challenging situations",
    category: "crisis",
  },
};

export const CreateScenarioModal: React.FC<CreateScenarioModalProps> = ({
  open,
  onClose,
  onScenarioCreated,
  initialTemplate,
}) => {
  const { createScenario } = useScenarios();
  const { getScenarioTerm } = useTerminology();
  const allScenarios = useVisibleScenarios();
  const { currentUser, mode } = useApp();
  const currentUserName = currentUser.name;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<
    "growth" | "cost-cutting" | "crisis" | undefined
  >(undefined);
  const [depsExpanded, setDepsExpanded] = useState(false);
  const [selectedDependencies, setSelectedDependencies] = useState<
    { targetId: string; type: DependencyType }[]
  >([]);
  const [depTargetId, setDepTargetId] = useState<string>("");
  const [depType, setDepType] = useState<DependencyType>("prerequisite");

  useEffect(() => {
    if (open && initialTemplate) {
      const defaults = TEMPLATE_DEFAULTS[initialTemplate];
      if (defaults) {
        setName(defaults.name);
        setDescription(defaults.description);
        setCategory(defaults.category);
      }
    }
  }, [open, initialTemplate]);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }

    // Create the scenario with appropriate changes based on starting point
    // Base: Empty changes array (will be populated from base forecast in builder)
    // Blank: Empty changes array (user builds from scratch)
    const changes: any[] = [];

    const newScenarioId = await createScenario({
      name: trimmedName,
      description,
      status: "draft",
      priority: "medium",
      label: null,
      category: category || undefined,
      createdBy: currentUser.id,
      createdByName: currentUserName,
      modifiedBy: currentUserName,
      shares: [],
      isFavorite: false,
      changes,
      commentCount: 0,
      currentVersion: 1,
      collectionIds: [],
      metrics: {
        minCash: 0,
        minCashDate: new Date(),
        runway: 0,
        revenueChange: 0,
        revenueChangeAmount: 0,
        costChange: 0,
        costChangeAmount: 0,
        netImpact: 0,
        endingCash: 0,
      },
      tags: [],
      // Store starting point preference for builder to use
      startingPoint: "base",
      // Embed dependencies inside the scenario object so they travel as a single param
      // Always pass the array (even if empty) so the context can see the actual value
      _dependencies: selectedDependencies,
    });

    // Close modal first
    onClose();

    // Navigate to the scenario detail screen after a brief delay to ensure state updates
    setTimeout(() => {
      if (onScenarioCreated) {
        onScenarioCreated(newScenarioId);
      }
    }, 100);

    // Reset form
    setName("");
    setDescription("");
    setCategory(undefined);
    setSelectedDependencies([]);
    setDepsExpanded(false);
    setDepTargetId("");
    setDepType("prerequisite");
  };

  const isCreateDisabled = !name.trim();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New {getScenarioTerm(true, false)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="name">{getScenarioTerm(true, false)} Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Q1 2026 Growth Plan"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              placeholder={`Describe this ${getScenarioTerm(false)}...`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1.5"
            />
          </div>

          {/* Dependencies (optional, collapsible) */}
          <div className="border rounded-lg">
            <button
              type="button"
              onClick={() => setDepsExpanded(!depsExpanded)}
              className="flex items-center justify-between w-full p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
            >
              <span>
                Dependencies (optional)
                {selectedDependencies.length > 0 &&
                  ` — ${selectedDependencies.length} selected`}
              </span>
              {depsExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            {depsExpanded && (
              <div className="p-3 pt-0 space-y-3">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Dependency Type
                    </label>
                    <Select
                      value={depType}
                      onValueChange={(v) => setDepType(v as DependencyType)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prerequisite">
                          Prerequisite
                        </SelectItem>
                        <SelectItem value="conflict">Conflict</SelectItem>
                        <SelectItem value="enables">Enables</SelectItem>
                        <SelectItem value="enhanced-by">Enhanced By</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Target {getScenarioTerm(true, false)}
                    </label>
                    <ScenarioDependencyPicker
                      value={depTargetId}
                      onChange={setDepTargetId}
                      mode={mode}
                      excludeIds={selectedDependencies.map((d) => d.targetId)}
                      demoScenarios={allScenarios}
                      placeholder={`Search ${getScenarioTerm(true, false).toLowerCase()} by name...`}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={!depTargetId}
                      onClick={() => {
                        if (depTargetId) {
                          setSelectedDependencies((prev) => [
                            ...prev,
                            { targetId: depTargetId, type: depType },
                          ]);
                          setDepTargetId("");
                        }
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {selectedDependencies.length > 0 && (
                  <div className="space-y-1">
                    {selectedDependencies.map((dep, idx) => {
                      const target = allScenarios.find(
                        (s) => s.id === dep.targetId,
                      );
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                        >
                          <span>
                            <Badge variant="outline" className="mr-2">
                              {dep.type}
                            </Badge>
                            {target?.name ?? dep.targetId}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() =>
                              setSelectedDependencies((prev) =>
                                prev.filter((_, i) => i !== idx),
                              )
                            }
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreateDisabled}>
            Create {getScenarioTerm(true, false)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
